import { Component, Input, OnInit, Output, ViewChild, EventEmitter } from "@angular/core";
import { DemoAssetService } from "src/app/services/demo-asset.service";
import { AdminGridColumnsFactory } from "../admin-grid/admin-grid-columns-factory";
import { Router } from "@angular/router";
import { DemoAssetAclLookup, DemoAssetTemplateViewModel, DemoAssetViewModel } from "src/app/core/model/ContentManagement";
import { ToolbarItemFactory, ToolbarItem } from "../toolbar";
import { finalize, Observable } from "rxjs";
import { DxDataGridComponent } from "devextreme-angular";
import { ContentReadyEvent, RowDragging } from "devextreme/ui/data_grid";

@Component({
  selector: "brd-demos-grid",
  templateUrl: "./demos-grid.component.html",
  styleUrl: "./demos-grid.component.less",
  standalone: false
})
export class DemosGridComponent implements OnInit {
  private readonly SIMPLE_GB = Math.pow(1000, 3);
  private readonly SIMPLE_MB = Math.pow(1000, 2);
  private readonly SIMPLE_KB = 1000;

  private readonly REAL_GB = Math.pow(1024, 3);
  private readonly REAL_MB = Math.pow(1024, 2);
  private readonly REAL_KB = 1024;

  @Input() assets$: Observable<DemoAssetViewModel[]>;
  @Input() readonly = false;
  @Input() hideColumns: string[] = [];
  @Input() enableSelection = false;
  @Input() selectedKeys: number[];
  @Output() contentReadyEmitter = new EventEmitter<ContentReadyEvent>();
  @ViewChild(DxDataGridComponent) grid: DxDataGridComponent;

  columnsAsset = [];
  assetTemplates: DemoAssetTemplateViewModel[] = [];

  visible = false;
  templateVisible = false;
  submitting = false;

  selectedRowToDelete?: DemoAssetViewModel;
  deleteBtnOptions = {
    text: "Yes",
    onClick: (): void => {
      const idToDelete = this.selectedRowToDelete.id;

      this.hidePopup();
      this.submitting = true;
      this.demoAssetService
        .deleteAsset(idToDelete)
        .pipe(
          finalize(() => {
            this.submitting = false;
          })
        )
        .subscribe();
    }
  };
  cancelBtnOptions = {
    text: "No",
    onClick: (): void => this.hidePopup()
  };
  editTemplatesBtnOptions = {
    text: "Edit templates",
    onClick: (): void => {
      for (const template of this.assetTemplates) {
        const url = this.router.createUrlTree(["/template", template.templateId]);
        window.open(url.toString(), "_blank");
      }

      this.hidePopup();
    }
  };

  toolbarItemsValues: ToolbarItem[] = [
    ToolbarItemFactory.createButton({
      icon: "add",
      text: "Board Demo",
      onClick: () => {
        this.addRow();
      }
    })
  ];

  public constructor(
    private demoAssetService: DemoAssetService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.columnsAsset = [
      AdminGridColumnsFactory.createButtonsColumn({
        buttons: [
          {
            icon: "edit",
            onClick: (e): void => {
              this.router.navigateByUrl(`/demo/${e.row.data.id}`);
            }
          }
        ]
      }),
      AdminGridColumnsFactory.createTextColumn({ fieldName: "id", caption: "id" }, { visible: false, allowEditing: false }),
      AdminGridColumnsFactory.createTextColumn(
        { fieldName: "name", caption: "Name" },
        {
          visible: !this.hideColumns.includes("name")
        }
      ),
      AdminGridColumnsFactory.createTextColumn(
        { fieldName: "description", caption: "Description" },
        { visible: !this.hideColumns.includes("description") }
      ),
      AdminGridColumnsFactory.createTextColumn(
        { fieldName: "owner", caption: "Created by" },
        { visible: !this.hideColumns.includes("owner") }
      ),
      AdminGridColumnsFactory.createDateTimeColumn(
        { fieldName: "creationDate", caption: "Creation date" },
        { visible: !this.hideColumns.includes("creationDate") }
      ),
      AdminGridColumnsFactory.createTextColumn(
        { fieldName: "updateBy", caption: "Update by" },
        { visible: !this.hideColumns.includes("updateBy") }
      ),
      AdminGridColumnsFactory.createDateTimeColumn(
        { fieldName: "lastUpdate", caption: "Last update" },
        { visible: !this.hideColumns.includes("lastUpdate") }
      ),
      AdminGridColumnsFactory.createTextColumn(
        { fieldName: "demoAssetSize", caption: "Size" },
        {
          calculateDisplayValue: (row: DemoAssetViewModel) => {
            const { demoAssetSize } = row;

            if (demoAssetSize > this.REAL_GB) {
              return `${parseFloat((demoAssetSize / this.REAL_GB).toFixed(2))} GB`;
            } else if (demoAssetSize > this.REAL_MB) {
              return `${parseFloat((demoAssetSize / this.REAL_MB).toFixed(2))} MB`;
            } else {
              return `${parseFloat((demoAssetSize / this.REAL_KB).toFixed(2))} KB`;
            }
          },
          visible: !this.hideColumns.includes("demoAssetSize")
        }
      ),
      AdminGridColumnsFactory.createTextColumn(
        { fieldName: "ramConsumption", caption: "Ram consumption" },
        {
          calculateDisplayValue: (row: DemoAssetViewModel) => {
            let { ramConsumption } = row;
            ramConsumption = ramConsumption * this.SIMPLE_MB;

            if (ramConsumption > this.SIMPLE_GB) {
              return `${parseFloat((ramConsumption / this.SIMPLE_GB).toFixed(2))} GB`;
            } else if (ramConsumption > this.SIMPLE_MB) {
              return `${parseFloat((ramConsumption / this.SIMPLE_MB).toFixed(2))} MB`;
            } else {
              return `${parseFloat((ramConsumption / this.SIMPLE_KB).toFixed(2))} KB`;
            }
          },
          visible: !this.hideColumns.includes("ramConsumption")
        }
      ),
      AdminGridColumnsFactory.createTextColumn(
        { fieldName: "diskConsumption", caption: "Disk consumption" },
        {
          calculateDisplayValue: (row: DemoAssetViewModel) => {
            let { diskConsumption } = row;
            diskConsumption = diskConsumption * this.SIMPLE_MB;

            if (diskConsumption > this.SIMPLE_GB) {
              return `${parseFloat((diskConsumption / this.SIMPLE_GB).toFixed(2))} GB`;
            } else if (diskConsumption > this.SIMPLE_MB) {
              return `${parseFloat((diskConsumption / this.SIMPLE_MB).toFixed(2))} MB`;
            } else {
              return `${parseFloat((diskConsumption / this.SIMPLE_KB).toFixed(2))} KB`;
            }
          },
          visible: !this.hideColumns.includes("diskConsumption")
        }
      ),
      AdminGridColumnsFactory.createLookupColumn<DemoAssetViewModel>(
        {
          fieldName: "aclType",
          caption: "Authorization",
          lookup: {
            dataSource: DemoAssetAclLookup,
            valueExpr: "value",
            displayExpr: "text"
          }
        },
        {
          width: 200,
          visible: !this.hideColumns.includes("aclType")
        }
      ),
      AdminGridColumnsFactory.createCheckboxColumn(
        { fieldName: "default", caption: "Default" },
        { visible: !this.hideColumns.includes("default") }
      ),
      AdminGridColumnsFactory.createCheckboxColumn(
        { fieldName: "draft", caption: "Draft" },
        { visible: !this.hideColumns.includes("draft") }
      )
    ];

    if (this.readonly) {
      this.columnsAsset.shift();
    } else {
      this.columnsAsset.push(
        AdminGridColumnsFactory.createButtonsColumn({
          name: "postbuttons",
          buttons: [
            {
              icon: "trash",
              onClick: ({ row: { data } }): void => {
                this.selectedRowToDelete = data;

                this.demoAssetService.getAssetTemplate(data.id).subscribe(res => {
                  this.assetTemplates = res;
                  this.visible = !this.assetTemplates.length;
                  this.templateVisible = !!this.assetTemplates.length;
                });
              }
            }
          ]
        })
      );
    }
  }

  addRow(): void {
    this.router.navigateByUrl("/demo/new");
  }

  hidePopup(): void {
    this.selectedRowToDelete = undefined;
    this.visible = false;
    this.templateVisible = false;
    this.assetTemplates = [];
  }

  onReorder: (e: Parameters<RowDragging["onReorder"]>[0]) => void = (e: Parameters<RowDragging["onReorder"]>[0]) => {
    const data = [...(e.component.option("dataSource") as DemoAssetViewModel[])];

    data.splice(e.fromIndex, 1);
    data.splice(e.toIndex, 0, e.itemData);
    e.component.option("dataSource", data);
    e.component.refresh();

    this.demoAssetService.updateOrders(data.map(x => x.id)).subscribe();
  };

  protected onGridContentReady(e: ContentReadyEvent): void {
    if (this.contentReadyEmitter) this.contentReadyEmitter.emit(e);
  }
}
